import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { useAuth } from '@/hooks/useAuth';
import { RootDashboardPage } from './RootDashboardPage';
import { UserSidebar } from '@/components/Layout/UserSidebar';
import { projectService } from '@/services/projectService';
import { dashboardService } from '@/services/dashboardService';
import { alarmService } from '@/services/alarmService';
import { logementsService, type LogementsSupervisionItem, type BuildingEquipmentStats } from '@/services/logementsService';
import { useTheme } from '@/hooks/useTheme';
import { ProjectLogo } from '@/components/ProjectLogo';
import { useProjectStore } from '@/store/projectStore';
import type { Alarm, Project } from '@/types';

type BuildingDashboardStats = {
  id: number;
  name: string;
  code: string | null;
  apartmentsCount: number;
  avgTemp: number | null;
  minTemp: { value: number; apartment: string; outOfRange: boolean } | null;
  maxTemp: { value: number; apartment: string; outOfRange: boolean } | null;
  avgHumidity: number | null;
  maxHumidity: { value: number; apartment: string } | null;
  avgSetpoint: number | null;
  onlineBoxes: number;
  offlineBoxes: number;
  onlineThermostats: number;
  offlineThermostats: number;
  apartments: Array<{
    id: number;
    name: string;
    currentTemp: number | null;
    currentHumidity: number | null;
    currentSetpoint: number | null;
    boxOnline: boolean | null;
    thermostatOnline: boolean | null;
  }>;
};

function average(values: number[]): number | null {
  if (values.length === 0) return null;
  return values.reduce((sum, value) => sum + value, 0) / values.length;
}

function formatMetric(value: number | null, unit = '', digits = 1): string {
  if (value === null || Number.isNaN(value)) return '—';
  return `${value.toFixed(digits)}${unit}`;
}

function getApartmentLabel(item: LogementsSupervisionItem): string {
  return item.apartment.name || item.apartment.number || item.apartment.code || `Appartement #${item.apartment.id}`;
}

export function DashboardPage() {
  const { isRoot, isAdmin } = useAuthStore();
  const { logout } = useAuth();
  const navigate = useNavigate();
  const { projectId } = useParams();
  const { isDark, toggleTheme } = useTheme();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { selectedProjectId, setSelectedProjectId } = useProjectStore();

  const scopedProjectId = projectId ? Number(projectId) : null;
  const [search, setSearch] = useState('');
  const isLanding = !scopedProjectId;

  useEffect(() => {
    if (isRoot()) {
      navigate('/admin/dashboard', { replace: true });
      return;
    }
  }, [isRoot, isAdmin, navigate]);

  // If user is ROLE_ROOT, show root dashboard
  if (isRoot()) {
    return <RootDashboardPage />;
  }

  // Fetch user's projects directly (API Platform filters by user memberships automatically)
  const { data: projectsResponse, isLoading: isLoadingProjects, error: projectsError } = useQuery({
    queryKey: ['user-projects'],
    queryFn: () => projectService.getProjects({ page: 1, itemsPerPage: 100 }),
    enabled: true,
    retry: 1,
  });

  const userProjects = useMemo(() => {
    return projectsResponse?.data || [];
  }, [projectsResponse]);

  // Redirect to last selected project when visiting /dashboard (if user still has access)
  useEffect(() => {
    if (!isLanding || isLoadingProjects || !selectedProjectId || userProjects.length === 0) return;
    const hasAccess = userProjects.some((p) => p.id === selectedProjectId);
    if (hasAccess) {
      navigate(`/dashboard/${selectedProjectId}`, { replace: true });
    }
  }, [isLanding, isLoadingProjects, selectedProjectId, userProjects, navigate]);

  const { data: openAlarmsResponse } = useQuery({
    queryKey: ['alarms', 'open', 'all'],
    queryFn: () => alarmService.getAlarms({ status: 'OPEN', page: 1, itemsPerPage: 500 }),
    enabled: isLanding && !isLoadingProjects && !projectsError,
    retry: 1,
  });

  const alarmsByProjectId = useMemo(() => {
    const map = new Map<number, number>();
    const alarms = (openAlarmsResponse?.data ?? []) as Alarm[];

    const getProjectIdFromAlarm = (alarm: Alarm): number | null => {
      const anyAlarm = alarm as any;
      const boxProjectId = anyAlarm?.box?.project?.id;
      const deviceProjectId = anyAlarm?.device?.box?.project?.id;
      const attributeProjectId = anyAlarm?.attribute?.device?.box?.project?.id;
      const projectIdValue = boxProjectId ?? deviceProjectId ?? attributeProjectId;
      return typeof projectIdValue === 'number' ? projectIdValue : null;
    };

    for (const a of alarms) {
      const pid = getProjectIdFromAlarm(a);
      if (!pid) continue;
      map.set(pid, (map.get(pid) ?? 0) + 1);
    }
    return map;
  }, [openAlarmsResponse]);

  const normalizedSearch = search.trim().toLowerCase();
  const filteredProjects = useMemo(() => {
    if (!normalizedSearch) return userProjects;
    return userProjects.filter((p: Project) => {
      const haystack = `${p.title ?? ''} ${p.city ?? ''} ${p.adresse ?? ''}`.toLowerCase();
      return haystack.includes(normalizedSearch);
    });
  }, [userProjects, normalizedSearch]);

  const currentProject = useMemo(() => {
    if (!scopedProjectId) return null;
    return userProjects.find((p) => p.id === scopedProjectId) ?? null;
  }, [userProjects, scopedProjectId]);

  const showSidebar = !!scopedProjectId;

  // Fetch dashboard statistics (scoped by project)
  const { data: dashboardStats, isLoading: isLoadingStats } = useQuery({
    queryKey: ['dashboard-stats', scopedProjectId],
    queryFn: () => dashboardService.getStats(scopedProjectId ?? undefined),
    enabled: !!scopedProjectId && !isLoadingProjects && !projectsError,
    retry: 1,
  });

  const {
    data: logementsSupervision,
    isLoading: isLoadingBuildingStats,
    isError: isBuildingStatsError,
  } = useQuery({
    queryKey: ['dashboard-buildings-supervision', scopedProjectId],
    queryFn: () => logementsService.getSupervision({ projectId: scopedProjectId!, itemsPerPage: 1000 }),
    enabled: !!scopedProjectId && !isLoadingProjects && !projectsError,
    retry: 1,
  });

  const buildingStats = useMemo<BuildingDashboardStats[]>(() => {
    const items = logementsSupervision?.items ?? [];
    if (items.length === 0) return [];

    const equipmentByBuilding = new Map<number, BuildingEquipmentStats>();
    for (const stats of logementsSupervision?.buildingsEquipment ?? []) {
      equipmentByBuilding.set(stats.buildingId, stats);
    }

    const grouped = new Map<number, LogementsSupervisionItem[]>();
    for (const item of items) {
      const group = grouped.get(item.building.id);
      if (group) {
        group.push(item);
      } else {
        grouped.set(item.building.id, [item]);
      }
    }

    return Array.from(grouped.values())
      .map((groupItems) => {
        const first = groupItems[0];
        const temperatures = groupItems
          .map((item) => item.metrics.tempInterieur)
          .filter((value): value is number => value !== null);
        const humidities = groupItems
          .map((item) => item.metrics.humidity)
          .filter((value): value is number => value !== null);
        const setpoints = groupItems
          .map((item) => item.metrics.tempConsigne)
          .filter((value): value is number => value !== null);

        const minTempItem = groupItems.reduce<LogementsSupervisionItem | null>((current, item) => {
          if (item.metrics.tempInterieur === null) return current;
          if (!current || current.metrics.tempInterieur === null || item.metrics.tempInterieur < current.metrics.tempInterieur) {
            return item;
          }
          return current;
        }, null);

        const maxTempItem = groupItems.reduce<LogementsSupervisionItem | null>((current, item) => {
          if (item.metrics.tempInterieur === null) return current;
          if (!current || current.metrics.tempInterieur === null || item.metrics.tempInterieur > current.metrics.tempInterieur) {
            return item;
          }
          return current;
        }, null);

        const maxHumidityItem = groupItems.reduce<LogementsSupervisionItem | null>((current, item) => {
          if (item.metrics.humidity === null) return current;
          if (!current || current.metrics.humidity === null || item.metrics.humidity > current.metrics.humidity) {
            return item;
          }
          return current;
        }, null);

        // Fallback (older API without buildingsEquipment): derive counts from the boxes/thermostats
        // resolvable at the apartment level only. Under-counts equipment not linked to an apartment.
        const boxStates = new Map<number, boolean | null>();
        const thermostatStates = new Map<number, boolean | null>();
        for (const item of groupItems) {
          if (item.box.id !== null && !boxStates.has(item.box.id)) {
            boxStates.set(item.box.id, item.box.isOnline);
          }
          if (item.thermostat.deviceId !== null && !thermostatStates.has(item.thermostat.deviceId)) {
            thermostatStates.set(item.thermostat.deviceId, item.thermostat.isOnline);
          }
        }
        const equipment = equipmentByBuilding.get(first.building.id);

        return {
          id: first.building.id,
          name: first.building.name || `Bâtiment #${first.building.id}`,
          code: first.building.code,
          apartmentsCount: groupItems.length,
          avgTemp: average(temperatures),
          minTemp: minTempItem && minTempItem.metrics.tempInterieur !== null
            ? {
                value: minTempItem.metrics.tempInterieur,
                apartment: getApartmentLabel(minTempItem),
                outOfRange: minTempItem.metrics.tempInterieurOutOfRange,
              }
            : null,
          maxTemp: maxTempItem && maxTempItem.metrics.tempInterieur !== null
            ? {
                value: maxTempItem.metrics.tempInterieur,
                apartment: getApartmentLabel(maxTempItem),
                outOfRange: maxTempItem.metrics.tempInterieurOutOfRange,
              }
            : null,
          avgHumidity: average(humidities),
          maxHumidity: maxHumidityItem && maxHumidityItem.metrics.humidity !== null
            ? {
                value: maxHumidityItem.metrics.humidity,
                apartment: getApartmentLabel(maxHumidityItem),
              }
            : null,
          avgSetpoint: average(setpoints),
          onlineBoxes: equipment?.boxOnline ?? Array.from(boxStates.values()).filter((state) => state === true).length,
          offlineBoxes: equipment?.boxOffline ?? Array.from(boxStates.values()).filter((state) => state === false).length,
          onlineThermostats: equipment?.thermostatOnline ?? Array.from(thermostatStates.values()).filter((state) => state === true).length,
          offlineThermostats: equipment?.thermostatOffline ?? Array.from(thermostatStates.values()).filter((state) => state === false).length,
          apartments: [...groupItems]
            .sort((a, b) => getApartmentLabel(a).localeCompare(getApartmentLabel(b), 'fr'))
            .map((item) => ({
              id: item.apartment.id,
              name: getApartmentLabel(item),
              currentTemp: item.metrics.tempInterieur,
              currentHumidity: item.metrics.humidity,
              currentSetpoint: item.metrics.tempConsigne,
              boxOnline: item.box.isOnline,
              thermostatOnline: item.thermostat.isOnline,
            })),
        };
      })
      .sort((a, b) => a.name.localeCompare(b.name, 'fr'));
  }, [logementsSupervision]);

  if (isLoadingProjects) {
    return (
      <div className="flex h-screen bg-slate-100 dark:bg-slate-900">
        {showSidebar && <UserSidebar projectId={scopedProjectId ?? undefined} isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />}
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <span className="material-icons-outlined animate-spin text-primary text-4xl">progress_activity</span>
            <p className="mt-4 text-slate-400">Chargement...</p>
          </div>
        </div>
      </div>
    );
  }

  if (projectsError || userProjects.length === 0) {
    // Admin : redirection via useEffect (affiche loader en attendant)
    if (isAdmin()) {
      return (
        <div className="flex h-screen bg-slate-100 dark:bg-slate-900 items-center justify-center">
          <span className="material-icons-outlined animate-spin text-primary text-4xl">progress_activity</span>
        </div>
      );
    }
    // User et Manager uniquement : afficher le message
    return (
      <div className="flex h-screen bg-slate-100 dark:bg-slate-900">
        {showSidebar && <UserSidebar projectId={scopedProjectId ?? undefined} isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />}
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="text-center max-w-md">
            <span className="material-icons-outlined text-red-500 text-6xl mb-4">error</span>
            <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-2">Aucun projet assigné</h2>
            <p className="text-slate-400 mb-6">
              Vous n'avez pas encore accès à un projet. Contactez votre administrateur.
            </p>
            <button
              onClick={logout}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-200 bg-slate-200 dark:bg-slate-700 hover:bg-slate-300 dark:hover:bg-slate-600 rounded-lg transition-colors"
            >
              <span className="material-icons-outlined">logout</span>
              Se déconnecter
            </button>
          </div>
        </div>
      </div>
    );
  }

  // -------------------------
  // Landing page: /dashboard
  // -------------------------
  if (!scopedProjectId) {
    return (
      <div className="h-screen bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-100 flex overflow-hidden font-display">
        <div className="flex-1 min-w-0 overflow-y-auto">
          <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
            <div className="flex items-center justify-between gap-3 mb-6">
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 rounded-2xl bg-primary/15 border border-primary/30 flex items-center justify-center">
                  <span className="material-icons-outlined text-primary">domain</span>
                </div>
                <div>
                  <div className="text-[11px] text-slate-500 dark:text-slate-400 font-bold uppercase tracking-widest">SMALT GTB</div>
                  <h1 className="text-xl sm:text-2xl font-extrabold tracking-tight">Vos projets</h1>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={toggleTheme}
                  className="p-2 rounded-full text-slate-500 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-800/60 hover:text-slate-900 dark:hover:text-white transition-colors"
                  title={isDark ? 'Activer le mode clair' : 'Activer le mode sombre'}
                >
                  {isDark ? <span className="material-icons-outlined">light_mode</span> : <span className="material-icons-outlined">dark_mode</span>}
                </button>
              </div>
            </div>

            <div className="mb-5">
              <div className="relative">
                <span className="material-icons-outlined absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 dark:text-slate-400">search</span>
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Rechercher un projet… (nom ou ville)"
                  className="w-full pl-12 pr-4 py-3 rounded-2xl bg-white dark:bg-slate-900/60 border border-slate-200 dark:border-slate-800 focus:outline-none focus:ring-2 focus:ring-primary/60 focus:border-primary/40 text-slate-900 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500"
                />
              </div>
            </div>

            <div className="space-y-3">
              {filteredProjects.length === 0 ? (
                <div className="text-center text-slate-500 dark:text-slate-400 py-10">
                  Aucun projet ne correspond à votre recherche.
                </div>
              ) : (
                filteredProjects.map((p) => {
                  const alarmCount = alarmsByProjectId.get(p.id) ?? 0;
                  const hasAlarms = alarmCount > 0;
                  const addressLine = [p.adresse, p.city].filter(Boolean).join(', ');

                  return (
                    <button
                      key={p.id}
                      onClick={() => {
                        setSelectedProjectId(p.id);
                        navigate(`/dashboard/${p.id}`, { replace: true });
                      }}
                      className="w-full text-left bg-white dark:bg-slate-900/40 border border-slate-200 dark:border-slate-800 rounded-3xl px-4 py-4 hover:bg-slate-50 dark:hover:bg-slate-900/60 hover:border-slate-300 dark:hover:border-slate-700 transition-colors"
                    >
                      <div className="flex items-center justify-between gap-4">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-11 h-11 rounded-2xl bg-slate-100 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 flex items-center justify-center flex-shrink-0">
                            <span className="material-icons-outlined text-primary dark:text-cyan-300">business</span>
                          </div>
                          <div className="min-w-0">
                            <div className="font-extrabold text-lg leading-tight truncate text-slate-900 dark:text-white">{p.title}</div>
                            <div className="text-sm text-slate-500 dark:text-slate-400 truncate">{addressLine || '—'}</div>
                          </div>
                        </div>

                        <div className="flex items-center gap-3 flex-shrink-0">
                          <div
                            className={[
                              'px-3 py-1 rounded-full text-xs font-extrabold tracking-wide border',
                              hasAlarms
                                ? 'bg-rose-500/15 text-rose-600 dark:text-rose-200 border-rose-500/30'
                                : 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-200 border-emerald-500/30',
                            ].join(' ')}
                          >
                            {hasAlarms ? `${alarmCount} ALARMES` : 'AUCUNE ALARME'}
                          </div>
                          <div className="flex items-center gap-1 text-primary dark:text-cyan-300 font-bold">
                            <span className="text-sm">ACCÉDER</span>
                            <span className="material-icons-outlined">chevron_right</span>
                          </div>
                        </div>
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </main>
        </div>
      </div>
    );
  }

  if (scopedProjectId && !currentProject) {
    return (
      <div className="flex h-screen bg-slate-100 dark:bg-slate-900">
        <UserSidebar projectId={scopedProjectId} isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="text-center max-w-md">
            <span className="material-icons-outlined text-red-500 text-6xl mb-4">error</span>
            <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-2">Projet introuvable</h2>
            <p className="text-slate-400 mb-4">
              Ce projet n'est pas accessible avec votre compte.
            </p>
            <button
              onClick={() => navigate('/dashboard', { replace: true })}
              className="px-4 py-2 bg-primary text-white rounded-lg font-semibold hover:bg-primary/90 transition-colors"
            >
              Choisir un projet
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-slate-100 dark:bg-slate-900 text-slate-900 dark:text-slate-100 h-screen flex overflow-hidden font-display">
      {/* Sidebar */}
      {showSidebar && <UserSidebar projectId={scopedProjectId ?? undefined} isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />}

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden relative">
        {/* Header */}
        <header className="h-16 bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between px-6 sm:px-8 z-20 shadow-sm sticky top-0">
          <div>
            <div className="text-[10px] text-slate-500 dark:text-slate-400 font-bold uppercase tracking-widest mb-0.5">
              Site
            </div>
            <h1 className="text-lg font-bold text-slate-900 dark:text-white leading-none tracking-tight flex items-center gap-2">
              <>
                {currentProject.title} • {currentProject.city || '—'}
                <button
                  onClick={() => navigate('/dashboard')}
                  className="ml-2 text-xs font-semibold px-2 py-1 rounded-md bg-slate-100 dark:bg-slate-700/60 border border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-200 hover:border-slate-300 dark:hover:border-slate-500 transition-colors"
                  title="Changer de projet"
                >
                  Changer
                </button>
              </>
            </h1>
          </div>
          <div className="flex items-center space-x-4">
            <div className="hidden md:flex items-center px-3 py-1 bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-full text-xs font-medium text-slate-600 dark:text-slate-300">
              <span className="w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse shadow-sm shadow-green-500/50"></span>
              Système en ligne
            </div>
            <div className="h-8 w-px bg-slate-200 dark:bg-slate-700 mx-1"></div>
            <ProjectLogo project={currentProject} size="md" className="rounded-lg" />
            <button
              onClick={toggleTheme}
              className="p-2 rounded-full text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 hover:text-primary transition-colors"
              title={isDark ? 'Activer le mode clair' : 'Activer le mode sombre'}
            >
              {isDark ? <span className="material-icons-outlined">light_mode</span> : <span className="material-icons-outlined">dark_mode</span>}
            </button>
          </div>
        </header>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-0 scroll-smooth">
          <main className="max-w-7xl mx-auto px-6 sm:px-8 py-8 w-full">
            {/* Statistics Cards */}
            {isLoadingStats ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6 mb-8">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="bg-white dark:bg-slate-800 rounded-xl shadow-sm p-6 border border-slate-200 dark:border-slate-700 animate-pulse">
                    <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-24 mb-4"></div>
                    <div className="h-8 bg-slate-200 dark:bg-slate-700 rounded w-16"></div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-6 mb-8">
                {/* Nombre de projets */}
                <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm p-6 border border-slate-200 dark:border-slate-700 relative overflow-hidden group hover:border-slate-300 dark:hover:border-slate-600 transition-colors">
                  <div className="absolute right-0 top-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                    <span className="material-icons-outlined text-6xl text-primary" style={{ fontSize: '48px' }}>folder</span>
                  </div>
                  <h3 className="text-slate-500 dark:text-slate-400 text-sm font-medium mb-2">Projets</h3>
                  <div className="flex items-baseline">
                    <p className="text-3xl font-bold text-slate-900 dark:text-white">{dashboardStats?.projects || 0}</p>
                  </div>
                </div>

                {/* Nombre de bâtiments */}
                <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm p-6 border border-slate-200 dark:border-slate-700 relative overflow-hidden group hover:border-slate-300 dark:hover:border-slate-600 transition-colors">
                  <div className="absolute right-0 top-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                    <span className="material-icons-outlined text-6xl text-blue-500" style={{ fontSize: '48px' }}>business</span>
                  </div>
                  <h3 className="text-slate-500 dark:text-slate-400 text-sm font-medium mb-2">Bâtiments</h3>
                  <div className="flex items-baseline">
                    <p className="text-3xl font-bold text-slate-900 dark:text-white">{dashboardStats?.buildings || 0}</p>
                  </div>
                </div>

                {/* Nombre d'appartements */}
                <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm p-6 border border-slate-200 dark:border-slate-700 relative overflow-hidden group hover:border-slate-300 dark:hover:border-slate-600 transition-colors">
                  <div className="absolute right-0 top-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                    <span className="material-icons-outlined text-6xl text-purple-500" style={{ fontSize: '48px' }}>apartment</span>
                  </div>
                  <h3 className="text-slate-500 dark:text-slate-400 text-sm font-medium mb-2">Appartements</h3>
                  <div className="flex items-baseline">
                    <p className="text-3xl font-bold text-slate-900 dark:text-white">{dashboardStats?.apartments || 0}</p>
                  </div>
                </div>

                {/* Boxes en ligne / hors ligne */}
                <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm p-6 border border-slate-200 dark:border-slate-700 relative overflow-hidden group hover:border-slate-300 dark:hover:border-slate-600 transition-colors">
                  <div className="absolute right-0 top-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                    <span className="material-icons-outlined text-6xl text-green-500" style={{ fontSize: '48px' }}>dns</span>
                  </div>
                  <h3 className="text-slate-500 dark:text-slate-400 text-sm font-medium mb-2">Boxes</h3>
                  <div className="flex items-baseline gap-2">
                    <p className="text-3xl font-bold text-green-500">{dashboardStats?.boxes?.online || 0}</p>
                    <span className="text-slate-400 dark:text-slate-500">/</span>
                    <p className="text-2xl font-semibold text-red-500">{dashboardStats?.boxes?.offline || 0}</p>
                  </div>
                  <p className="mt-2 text-xs text-slate-400 dark:text-slate-500">
                    <span className="text-green-500">En ligne</span> / <span className="text-red-500">Hors ligne</span>
                  </p>
                </div>

                {/* Thermostats en ligne / hors ligne */}
                <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm p-6 border border-slate-200 dark:border-slate-700 relative overflow-hidden group hover:border-slate-300 dark:hover:border-slate-600 transition-colors">
                  <div className="absolute right-0 top-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                    <span className="material-icons-outlined text-6xl text-orange-500" style={{ fontSize: '48px' }}>thermostat</span>
                  </div>
                  <h3 className="text-slate-500 dark:text-slate-400 text-sm font-medium mb-2">Thermostats</h3>
                  <div className="flex items-baseline gap-2">
                    <p className="text-3xl font-bold text-green-500">{dashboardStats?.thermostats?.online || 0}</p>
                    <span className="text-slate-400 dark:text-slate-500">/</span>
                    <p className="text-2xl font-semibold text-red-500">{dashboardStats?.thermostats?.offline || 0}</p>
                  </div>
                  <p className="mt-2 text-xs text-slate-400 dark:text-slate-500">
                    <span className="text-green-500">En ligne</span> / <span className="text-red-500">Hors ligne</span>
                  </p>
                </div>

                <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm p-6 border border-slate-200 dark:border-slate-700 relative overflow-hidden group hover:border-slate-300 dark:hover:border-slate-600 transition-colors">
                  <div className="absolute right-0 top-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                    <span className="material-icons-outlined text-6xl text-cyan-500" style={{ fontSize: '48px' }}>tune</span>
                  </div>
                  <h3 className="text-slate-500 dark:text-slate-400 text-sm font-medium mb-2">Consigne globale</h3>
                  <div className="flex items-baseline">
                    <p className="text-3xl font-bold text-slate-900 dark:text-white">{formatMetric(dashboardStats?.heatingSetpoint ?? null, '°C')}</p>
                  </div>
                </div>

              </div>
            )}

            <section className="space-y-6">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <div className="text-[10px] text-slate-500 dark:text-slate-400 font-bold uppercase tracking-widest mb-1">
                    Vue par bâtiment
                  </div>
                  <h2 className="text-2xl font-bold text-slate-900 dark:text-white">Statistiques instantanées</h2>
                  <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                    Température, humidité et consigne agrégées à partir des logements du bâtiment.
                  </p>
                </div>
              </div>

              {isLoadingBuildingStats ? (
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                  {[...Array(2)].map((_, i) => (
                    <div key={i} className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm p-6 border border-slate-200 dark:border-slate-700 animate-pulse">
                      <div className="h-6 bg-slate-200 dark:bg-slate-700 rounded w-48 mb-6"></div>
                      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                        {[...Array(4)].map((__, j) => (
                          <div key={j} className="h-24 rounded-xl bg-slate-100 dark:bg-slate-700/60"></div>
                        ))}
                      </div>
                      <div className="h-40 rounded-xl bg-slate-100 dark:bg-slate-700/60"></div>
                    </div>
                  ))}
                </div>
              ) : isBuildingStatsError ? (
                <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm p-6 border border-rose-200 dark:border-rose-900/60">
                  <div className="flex items-start gap-3">
                    <span className="material-icons-outlined text-rose-500">error</span>
                    <div>
                      <h3 className="font-semibold text-slate-900 dark:text-white">Impossible de charger les statistiques par bâtiment</h3>
                      <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                        Les compteurs globaux restent disponibles, mais la supervision des logements n’a pas pu être récupérée.
                      </p>
                    </div>
                  </div>
                </div>
              ) : buildingStats.length === 0 ? (
                <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm p-6 border border-slate-200 dark:border-slate-700 text-sm text-slate-500 dark:text-slate-400">
                  Aucune donnée instantanée disponible pour afficher les bâtiments.
                </div>
              ) : (
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                  {buildingStats.map((building) => (
                    <section key={building.id} className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden">
                      <div className="px-6 py-5 border-b border-slate-200 dark:border-slate-700">
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <h3 className="text-lg font-bold text-slate-900 dark:text-white">{building.name}</h3>
                            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                              {building.apartmentsCount} appartement{building.apartmentsCount > 1 ? 's' : ''}
                              {building.code ? ` • ${building.code}` : ''}
                            </p>
                          </div>
                          <div className="flex flex-wrap justify-end gap-2 text-xs">
                            <span className="px-3 py-1 rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-300 border border-emerald-500/20">
                              Boxes: {building.onlineBoxes} online
                            </span>
                            <span className="px-3 py-1 rounded-full bg-orange-500/10 text-orange-600 dark:text-orange-300 border border-orange-500/20">
                              Thermostats: {building.onlineThermostats} online
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="p-6 space-y-6">
                        {/* Synthèse : moyennes en avant, extrêmes en pastilles discrètes */}
                        <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-slate-50/60 dark:bg-slate-900/40 p-5">
                          <div className="grid grid-cols-3 divide-x divide-slate-200 dark:divide-slate-700">
                            <div className="flex items-center gap-3 px-2 sm:px-4 min-w-0">
                              <div className="w-10 h-10 rounded-full bg-orange-500/10 flex items-center justify-center shrink-0">
                                <span className="material-icons-outlined text-orange-500" style={{ fontSize: '20px' }}>thermostat</span>
                              </div>
                              <div className="min-w-0">
                                <div className="text-lg sm:text-xl font-bold text-slate-900 dark:text-white leading-tight truncate">
                                  {formatMetric(building.avgTemp, '°C')}
                                </div>
                                <div className="text-[11px] text-slate-500 dark:text-slate-400 truncate">Temp. moyenne</div>
                              </div>
                            </div>
                            <div className="flex items-center gap-3 px-2 sm:px-4 min-w-0">
                              <div className="w-10 h-10 rounded-full bg-cyan-500/10 flex items-center justify-center shrink-0">
                                <span className="material-icons-outlined text-cyan-500" style={{ fontSize: '20px' }}>water_drop</span>
                              </div>
                              <div className="min-w-0">
                                <div className="text-lg sm:text-xl font-bold text-slate-900 dark:text-white leading-tight truncate">
                                  {formatMetric(building.avgHumidity, '%')}
                                </div>
                                <div className="text-[11px] text-slate-500 dark:text-slate-400 truncate">Humidité moyenne</div>
                              </div>
                            </div>
                            <div className="flex items-center gap-3 px-2 sm:px-4 min-w-0">
                              <div className="w-10 h-10 rounded-full bg-yellow-500/10 flex items-center justify-center shrink-0">
                                <span className="material-icons-outlined text-yellow-500" style={{ fontSize: '20px' }}>tune</span>
                              </div>
                              <div className="min-w-0">
                                <div className="text-lg sm:text-xl font-bold text-slate-900 dark:text-white leading-tight truncate">
                                  {formatMetric(building.avgSetpoint, '°C')}
                                </div>
                                <div className="text-[11px] text-slate-500 dark:text-slate-400 truncate">Consigne moyenne</div>
                              </div>
                            </div>
                          </div>

                          <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
                            <span className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full bg-blue-500/10 text-blue-600 dark:text-blue-300">
                              <span className="material-icons-outlined" style={{ fontSize: '14px' }}>arrow_downward</span>
                              Min {building.minTemp ? formatMetric(building.minTemp.value, '°C') : '—'}
                              {building.minTemp && <span className="opacity-70 hidden sm:inline">· {building.minTemp.apartment}</span>}
                              {building.minTemp?.outOfRange && (
                                <span title="Valeur capteur hors plage plausible (7-36°C), affichage limité">⚠️</span>
                              )}
                            </span>
                            <span className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full bg-rose-500/10 text-rose-600 dark:text-rose-300">
                              <span className="material-icons-outlined" style={{ fontSize: '14px' }}>arrow_upward</span>
                              Max {building.maxTemp ? formatMetric(building.maxTemp.value, '°C') : '—'}
                              {building.maxTemp && <span className="opacity-70 hidden sm:inline">· {building.maxTemp.apartment}</span>}
                              {building.maxTemp?.outOfRange && (
                                <span title="Valeur capteur hors plage plausible (7-36°C), affichage limité">⚠️</span>
                              )}
                            </span>
                            <span className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full bg-cyan-500/10 text-cyan-600 dark:text-cyan-300">
                              <span className="material-icons-outlined" style={{ fontSize: '14px' }}>water_drop</span>
                              Humidité max {building.maxHumidity ? formatMetric(building.maxHumidity.value, '%') : '—'}
                              {building.maxHumidity && <span className="opacity-70 hidden sm:inline">· {building.maxHumidity.apartment}</span>}
                            </span>
                          </div>
                        </div>

                        {/* Liste des logements : layout en flex-wrap pour rester lisible sans scroll horizontal */}
                        <div className="rounded-xl border border-slate-200 dark:border-slate-700 divide-y divide-slate-200 dark:divide-slate-700 overflow-hidden">
                          {building.apartments.map((apartment) => {
                            const boxOnline = apartment.boxOnline;
                            const thermoOnline = apartment.thermostatOnline;
                            const statusDot =
                              boxOnline === false || thermoOnline === false
                                ? 'bg-rose-500'
                                : boxOnline === true || thermoOnline === true
                                  ? 'bg-emerald-400'
                                  : 'bg-slate-300 dark:bg-slate-600';

                            return (
                              <div
                                key={apartment.id}
                                className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3 text-sm hover:bg-slate-50 dark:hover:bg-slate-900/40 transition-colors"
                              >
                                <div className="flex items-center gap-2 min-w-[110px] flex-1">
                                  <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${statusDot}`} />
                                  <span className="font-semibold text-slate-800 dark:text-slate-100 truncate">{apartment.name}</span>
                                </div>

                                <div className="flex items-center gap-1.5 text-slate-700 dark:text-slate-200" title="Température intérieure">
                                  <span className="material-icons-outlined text-orange-400" style={{ fontSize: '16px' }}>thermostat</span>
                                  <span className="font-semibold">{formatMetric(apartment.currentTemp, '°C')}</span>
                                </div>

                                <div className="flex items-center gap-1.5 text-slate-500 dark:text-slate-400" title="Consigne">
                                  <span className="material-icons-outlined text-yellow-400" style={{ fontSize: '16px' }}>tune</span>
                                  <span>{formatMetric(apartment.currentSetpoint, '°C')}</span>
                                </div>

                                <div className="flex items-center gap-1.5 text-slate-500 dark:text-slate-400" title="Humidité">
                                  <span className="material-icons-outlined text-cyan-400" style={{ fontSize: '16px' }}>water_drop</span>
                                  <span>{formatMetric(apartment.currentHumidity, '%')}</span>
                                </div>

                                <div className="flex items-center gap-3 sm:ml-auto text-xs">
                                  <span
                                    className={`flex items-center gap-1 font-medium ${
                                      boxOnline === true
                                        ? 'text-emerald-600 dark:text-emerald-300'
                                        : boxOnline === false
                                          ? 'text-rose-600 dark:text-rose-300'
                                          : 'text-slate-400 dark:text-slate-500'
                                    }`}
                                    title={`Box ${boxOnline === true ? 'en ligne' : boxOnline === false ? 'hors ligne' : 'inconnue'}`}
                                  >
                                    <span className="material-icons-outlined" style={{ fontSize: '15px' }}>dns</span>
                                    Box
                                  </span>
                                  <span
                                    className={`flex items-center gap-1 font-medium ${
                                      thermoOnline === true
                                        ? 'text-emerald-600 dark:text-emerald-300'
                                        : thermoOnline === false
                                          ? 'text-rose-600 dark:text-rose-300'
                                          : 'text-slate-400 dark:text-slate-500'
                                    }`}
                                    title={`Thermostat ${thermoOnline === true ? 'en ligne' : thermoOnline === false ? 'hors ligne' : 'inconnu'}`}
                                  >
                                    <span className="material-icons-outlined" style={{ fontSize: '15px' }}>thermostat</span>
                                    Thermo
                                  </span>
                                </div>
                              </div>
                            );
                          })}
                        </div>

                        <div className="flex flex-wrap gap-2 text-xs text-slate-500 dark:text-slate-400">
                          <span className="px-3 py-1 rounded-full bg-slate-100 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-700">
                            {building.offlineBoxes} box{building.offlineBoxes > 1 ? 'es' : ''} offline
                          </span>
                          <span className="px-3 py-1 rounded-full bg-slate-100 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-700">
                            {building.offlineThermostats} thermostat{building.offlineThermostats > 1 ? 's' : ''} offline
                          </span>
                        </div>
                      </div>
                    </section>
                  ))}
                </div>
              )}
            </section>

          </main>
        </div>
      </div>

    </div>
  );
}
